import { omit } from 'lodash';
import { storeInsertItem, storeRemoveItem, storeUpdateObjectInArray } from '../utils/utils';

const initialState = {
  chains: [],
  selectedChainId: null,
  chainPermissions: [],
  chainMemberships: [],
  chainCareServices: [],
  chainCareServiceInvitations: [],
  chainCareServiceVehicles: [],
  maintenanceReservations: [],
  chainPartsAnalysis: [],
  chainPartsAnalysisPagination: null,
  chainPartsAnalysisSummary: null,
  chainsStatus: {
    loading: false,
    error: null,
  },
  chainStatus: {
    loading: false,
    error: null,
  },
  chainPermissionsStatus: {
    loading: false,
    error: null,
  },
  chainMembershipsStatus: {
    loading: false,
    error: null,
  },
  chainCareServicesStatus: {
    loading: false,
    error: null,
  },
  chainCareServiceVehiclesStatus: {
    loading: false,
    error: null,
  },
  maintenanceReservationsStatus: {
    loading: false,
    error: null,
  },
  chainPartsAnalysisStatus: {
    loading: false,
    error: null,
  },
  chainPartsAnalysisSummaryStatus: {
    loading: false,
    error: null,
  },
  reservationParts: {},
  chainMaintenanceDetails: {},
  vehicleApprovalStatuses: {},
  csMaintenanceReservations: [],
  csMaintenanceReservationsStatus: {
    loading: false,
    error: null,
  },
  chainInvoiceSettings: null,
  chainInvoiceSettingsStatus: {
    loading: false,
    error: null,
  },
  chainNetvisorSettings: null,
  chainNetvisorSettingsStatus: {
    loading: false,
    error: null,
  },
  chainNetvisorTestResult: null,
  chainCustomerMappings: [],
  chainCustomerMappingsStatus: {
    loading: false,
    error: null,
  },
  purchaseInvoices: [],
  purchaseInvoicesPagination: null,
  purchaseInvoicesStatus: {
    loading: false,
    error: null,
  },
  selectedPurchaseInvoice: null,
  selectedPurchaseInvoiceStatus: {
    loading: false,
    error: null,
  },
  purchaseInvoiceSyncStatus: {
    loading: false,
    error: null,
  },
  consolidatedInvoices: [],
  consolidatedInvoicesPagination: null,
  consolidatedInvoicesStatus: {
    loading: false,
    error: null,
  },
  selectedConsolidatedInvoice: null,
  selectedConsolidatedInvoiceStatus: {
    loading: false,
    error: null,
  },
  consolidatedInvoicePreview: null,
  consolidatedInvoicePreviewStatus: {
    loading: false,
    error: null,
  },
  chainAuditLogs: [],
  chainAuditLogsPagination: null,
  chainAuditLogsStatus: { loading: false, error: null },
  composeInvoiceStatus: {
    loading: false,
    error: null,
  },
  exportInvoiceStatus: {
    loading: false,
    error: null,
  },
  updateConsolidatedInvoiceStatus: {
    loading: false,
    error: null,
  },
  createCreditNoteStatus: {
    loading: false,
    error: null,
  },
  createdCreditNote: null,
  copyInvoiceStatus: {
    loading: false,
    error: null,
  },
  copiedInvoice: null,
  composePreview: null,
  composePreviewStatus: {
    loading: false,
    error: null,
  },
};

export default function chainReducer(state = initialState, action) {
  switch (action.type) {
    // Chains
    case 'FETCH_CHAINS_REQUEST':
      return {
        ...state,
        chainsStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAINS_SUCCESS':
      return {
        ...state,
        chains: action.payload,
        chainsStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAINS_FAIL':
      return {
        ...state,
        chains: [],
        chainsStatus: { loading: false, error: action.payload },
      };

    case 'FETCH_CHAIN_REQUEST':
      return {
        ...state,
        chainStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_SUCCESS': {
      const exists = state.chains.some(c => c.id === action.payload.id);
      return {
        ...state,
        chains: exists
          ? storeUpdateObjectInArray(state.chains, action.payload)
          : storeInsertItem(state.chains, action.payload),
        chainStatus: { loading: false, error: null },
      };
    }
    case 'FETCH_CHAIN_FAIL':
      return {
        ...state,
        chainStatus: { loading: false, error: action.payload },
      };

    case 'CREATE_CHAIN_SUCCESS':
      return {
        ...state,
        chains: storeInsertItem(state.chains, action.payload),
      };

    case 'UPDATE_CHAIN_SUCCESS':
      return {
        ...state,
        chains: storeUpdateObjectInArray(state.chains, action.payload),
      };

    case 'DELETE_CHAIN_SUCCESS':
      return {
        ...state,
        chains: storeRemoveItem(state.chains, action.additionalData.chainId),
      };

    // Chain Permissions
    case 'FETCH_CHAIN_PERMISSIONS_REQUEST':
      return {
        ...state,
        chainPermissionsStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_PERMISSIONS_SUCCESS':
      return {
        ...state,
        chainPermissions: action.payload,
        chainPermissionsStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_PERMISSIONS_FAIL':
      return {
        ...state,
        chainPermissions: [],
        chainPermissionsStatus: { loading: false, error: action.payload },
      };

    case 'CREATE_CHAIN_PERMISSION_SUCCESS':
      return {
        ...state,
        chainPermissions: storeInsertItem(state.chainPermissions, action.payload),
      };

    case 'UPDATE_CHAIN_PERMISSION_SUCCESS':
      return {
        ...state,
        chainPermissions: storeUpdateObjectInArray(state.chainPermissions, action.payload),
      };

    case 'DELETE_CHAIN_PERMISSION_SUCCESS':
      return {
        ...state,
        chainPermissions: storeRemoveItem(state.chainPermissions, action.additionalData.permissionId),
      };

    // Chain Memberships
    case 'FETCH_CHAIN_MEMBERSHIPS_REQUEST':
      return {
        ...state,
        chainMembershipsStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_MEMBERSHIPS_SUCCESS':
      return {
        ...state,
        chainMemberships: action.payload,
        chainMembershipsStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_MEMBERSHIPS_FAIL':
      return {
        ...state,
        chainMemberships: [],
        chainMembershipsStatus: { loading: false, error: action.payload },
      };

    case 'CREATE_CHAIN_MEMBERSHIP_SUCCESS':
      return {
        ...state,
        chainMemberships: storeInsertItem(state.chainMemberships, action.payload),
      };

    case 'UPDATE_CHAIN_MEMBERSHIP_SUCCESS':
      return {
        ...state,
        chainMemberships: storeUpdateObjectInArray(state.chainMemberships, action.payload),
      };

    case 'DELETE_CHAIN_MEMBERSHIP_SUCCESS':
      return {
        ...state,
        chainMemberships: storeRemoveItem(state.chainMemberships, action.additionalData.membershipId),
      };

    // Chain Care Services
    case 'FETCH_CHAIN_CARE_SERVICES_REQUEST':
      return {
        ...state,
        chainCareServicesStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_CARE_SERVICES_SUCCESS':
      return {
        ...state,
        chainCareServices: action.payload,
        chainCareServicesStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_CARE_SERVICES_FAIL':
      return {
        ...state,
        chainCareServices: [],
        chainCareServicesStatus: { loading: false, error: action.payload },
      };

    case 'INVITE_CHAIN_CARE_SERVICE_SUCCESS':
      return {
        ...state,
        chainCareServiceInvitations: storeInsertItem(state.chainCareServiceInvitations, action.payload),
      };

    case 'DELETE_CHAIN_CARE_SERVICE_SUCCESS':
      return {
        ...state,
        chainCareServices: storeRemoveItem(state.chainCareServices, action.additionalData.careServiceId),
      };

    // Chain Care Service Invitations
    case 'FETCH_CHAIN_CARE_SERVICE_INVITATIONS_SUCCESS':
      return {
        ...state,
        chainCareServiceInvitations: action.payload,
      };

    case 'ACCEPT_CHAIN_CARE_SERVICE_INVITATION_SUCCESS':
    case 'REJECT_CHAIN_CARE_SERVICE_INVITATION_SUCCESS':
      return {
        ...state,
        chainCareServiceInvitations: storeRemoveItem(state.chainCareServiceInvitations, action.payload),
      };

    // Chain Care Service Vehicles
    case 'FETCH_CHAIN_CARE_SERVICE_VEHICLES_REQUEST':
      return {
        ...state,
        chainCareServiceVehiclesStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_CARE_SERVICE_VEHICLES_SUCCESS':
      return {
        ...state,
        chainCareServiceVehicles: action.payload,
        chainCareServiceVehiclesStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_CARE_SERVICE_VEHICLES_FAIL':
      return {
        ...state,
        chainCareServiceVehicles: [],
        chainCareServiceVehiclesStatus: { loading: false, error: action.payload },
      };

    case 'ADD_CHAIN_CARE_SERVICE_VEHICLE_SUCCESS':
      return {
        ...state,
        chainCareServiceVehicles: storeInsertItem(state.chainCareServiceVehicles, action.payload),
      };

    case 'REMOVE_CHAIN_CARE_SERVICE_VEHICLE_SUCCESS':
      return {
        ...state,
        chainCareServiceVehicles: storeRemoveItem(state.chainCareServiceVehicles, action.additionalData.vehicleMappingId),
      };

    case 'ASSIGN_WORKSHOP_TO_VEHICLE_SUCCESS':
      return {
        ...state,
        chainCareServiceVehicles: storeUpdateObjectInArray(state.chainCareServiceVehicles, action.payload),
      };

    // Maintenance Reservations
    case 'FETCH_MAINTENANCE_RESERVATIONS_REQUEST':
      return {
        ...state,
        maintenanceReservationsStatus: { loading: true, error: null },
      };
    case 'FETCH_MAINTENANCE_RESERVATIONS_SUCCESS':
      return {
        ...state,
        maintenanceReservations: action.payload,
        maintenanceReservationsStatus: { loading: false, error: null },
      };
    case 'FETCH_MAINTENANCE_RESERVATIONS_FAIL':
      return {
        ...state,
        maintenanceReservations: [],
        maintenanceReservationsStatus: { loading: false, error: action.payload },
      };

    // Reservation parts (for parts accordion in upcoming maintenances)
    case 'FETCH_RESERVATION_PARTS_REQUEST':
      return {
        ...state,
        reservationParts: {
          ...state.reservationParts,
          [action.additionalData.reservationId]: {
            data: null, loading: true, error: null,
          },
        },
      };
    case 'FETCH_RESERVATION_PARTS_SUCCESS':
      return {
        ...state,
        reservationParts: {
          ...state.reservationParts,
          [action.additionalData.reservationId]: {
            data: action.payload, loading: false, error: null,
          },
        },
      };
    case 'FETCH_RESERVATION_PARTS_FAIL':
      return {
        ...state,
        reservationParts: {
          ...state.reservationParts,
          [action.additionalData.reservationId]: {
            data: null, loading: false, error: action.payload,
          },
        },
      };

    // CS Maintenance Reservations (service portal context)
    case 'FETCH_CS_MAINTENANCE_RESERVATIONS_REQUEST':
      return {
        ...state,
        csMaintenanceReservationsStatus: { loading: true, error: null },
      };
    case 'FETCH_CS_MAINTENANCE_RESERVATIONS_SUCCESS':
      return {
        ...state,
        csMaintenanceReservations: action.payload,
        csMaintenanceReservationsStatus: { loading: false, error: null },
      };
    case 'FETCH_CS_MAINTENANCE_RESERVATIONS_FAIL':
      return {
        ...state,
        csMaintenanceReservations: [],
        csMaintenanceReservationsStatus: { loading: false, error: action.payload },
      };

    // Chain Parts Analysis
    case 'FETCH_CHAIN_PARTS_ANALYSIS_REQUEST':
      return {
        ...state,
        chainPartsAnalysisStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_PARTS_ANALYSIS_SUCCESS':
      return {
        ...state,
        chainPartsAnalysis: action.payload.results || action.payload,
        chainPartsAnalysisPagination: action.payload.results
          ? omit(action.payload, 'results')
          : state.chainPartsAnalysisPagination,
        chainPartsAnalysisStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_PARTS_ANALYSIS_FAIL':
      return {
        ...state,
        chainPartsAnalysis: [],
        chainPartsAnalysisStatus: { loading: false, error: action.payload },
      };

    case 'FETCH_CHAIN_PARTS_ANALYSIS_SUMMARY_REQUEST':
      return {
        ...state,
        chainPartsAnalysisSummaryStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_PARTS_ANALYSIS_SUMMARY_SUCCESS':
      return {
        ...state,
        chainPartsAnalysisSummary: action.payload,
        chainPartsAnalysisSummaryStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_PARTS_ANALYSIS_SUMMARY_FAIL':
      return {
        ...state,
        chainPartsAnalysisSummary: null,
        chainPartsAnalysisSummaryStatus: { loading: false, error: action.payload },
      };

    // Chain maintenance details (for parts accordion)
    case 'FETCH_CHAIN_MAINTENANCE_DETAILS_REQUEST':
      return {
        ...state,
        chainMaintenanceDetails: {
          ...state.chainMaintenanceDetails,
          [action.additionalData.maintenanceId]: {
            data: null, loading: true, error: null,
          },
        },
      };
    case 'FETCH_CHAIN_MAINTENANCE_DETAILS_SUCCESS':
      return {
        ...state,
        chainMaintenanceDetails: {
          ...state.chainMaintenanceDetails,
          [action.additionalData.maintenanceId]: {
            data: action.payload, loading: false, error: null,
          },
        },
      };
    case 'FETCH_CHAIN_MAINTENANCE_DETAILS_FAIL':
      return {
        ...state,
        chainMaintenanceDetails: {
          ...state.chainMaintenanceDetails,
          [action.additionalData.maintenanceId]: {
            data: null, loading: false, error: action.payload,
          },
        },
      };

    // Vehicle approval statuses
    case 'FETCH_VEHICLE_APPROVAL_STATUSES_SUCCESS':
      return {
        ...state,
        vehicleApprovalStatuses: action.payload,
      };

    // Local state
    case 'SET_SELECTED_CHAIN':
      return {
        ...initialState,
        chains: state.chains,
        selectedChainId: action.chainId,
      };

    case 'CLEAR_CHAIN_STATE':
      return initialState;

    // Chain Invoice Settings
    case 'FETCH_CHAIN_INVOICE_SETTINGS_REQUEST':
      return {
        ...state,
        chainInvoiceSettingsStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_INVOICE_SETTINGS_SUCCESS':
      return {
        ...state,
        chainInvoiceSettings: action.payload,
        chainInvoiceSettingsStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_INVOICE_SETTINGS_FAIL':
      return {
        ...state,
        chainInvoiceSettingsStatus: { loading: false, error: action.payload },
      };
    case 'UPDATE_CHAIN_INVOICE_SETTINGS_SUCCESS':
      return {
        ...state,
        chainInvoiceSettings: action.payload,
      };

    // Chain Netvisor Settings
    case 'FETCH_CHAIN_NETVISOR_SETTINGS_REQUEST':
      return {
        ...state,
        chainNetvisorSettingsStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_NETVISOR_SETTINGS_SUCCESS':
      return {
        ...state,
        chainNetvisorSettings: action.payload,
        chainNetvisorSettingsStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_NETVISOR_SETTINGS_FAIL':
      return {
        ...state,
        chainNetvisorSettingsStatus: { loading: false, error: action.payload },
      };
    case 'UPDATE_CHAIN_NETVISOR_SETTINGS_SUCCESS':
      return {
        ...state,
        chainNetvisorSettings: action.payload,
      };
    case 'TEST_NETVISOR_CONNECTION_SUCCESS':
      return {
        ...state,
        chainNetvisorTestResult: action.payload,
      };
    case 'TEST_NETVISOR_CONNECTION_FAIL':
      // Without this the test button stays disabled ("...") forever after a
      // failed request (e.g. keys not yet configured -> HTTP 400).
      return {
        ...state,
        chainNetvisorTestResult: {
          success: false,
          message: (action.payload && (action.payload.detail || action.payload.message))
            || 'Connection test failed',
        },
      };

    // Chain Care Service Update
    case 'UPDATE_CHAIN_CARE_SERVICE_SUCCESS':
      return {
        ...state,
        chainCareServices: storeUpdateObjectInArray(state.chainCareServices, action.payload),
      };

    // Chain Customer Mappings
    case 'FETCH_CHAIN_CUSTOMER_MAPPINGS_REQUEST':
      return {
        ...state,
        chainCustomerMappingsStatus: { loading: true, error: null },
      };
    case 'FETCH_CHAIN_CUSTOMER_MAPPINGS_SUCCESS':
      return {
        ...state,
        chainCustomerMappings: action.payload,
        chainCustomerMappingsStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_CUSTOMER_MAPPINGS_FAIL':
      return {
        ...state,
        chainCustomerMappings: [],
        chainCustomerMappingsStatus: { loading: false, error: action.payload },
      };
    case 'DISCOVER_CUSTOMER_MAPPINGS_SUCCESS':
      return {
        ...state,
        chainCustomerMappings: action.payload.mappings || state.chainCustomerMappings,
      };
    case 'DISCOVER_SINGLE_CUSTOMER_MAPPING_SUCCESS': {
      var mapping = action.payload.mapping;
      if (mapping) {
        return {
          ...state,
          chainCustomerMappings: storeUpdateObjectInArray(state.chainCustomerMappings, mapping),
        };
      }
      return state;
    }
    case 'UPDATE_CUSTOMER_MAPPING_SUCCESS':
      return {
        ...state,
        chainCustomerMappings: storeUpdateObjectInArray(state.chainCustomerMappings, action.payload),
      };

    // Purchase Invoices
    case 'FETCH_PURCHASE_INVOICES_REQUEST':
      return {
        ...state,
        purchaseInvoicesStatus: { loading: true, error: null },
      };
    case 'FETCH_PURCHASE_INVOICES_SUCCESS':
      return {
        ...state,
        purchaseInvoices: action.payload.results || action.payload,
        purchaseInvoicesPagination: action.payload.results
          ? omit(action.payload, 'results')
          : state.purchaseInvoicesPagination,
        purchaseInvoicesStatus: { loading: false, error: null },
      };
    case 'FETCH_PURCHASE_INVOICES_FAIL':
      return {
        ...state,
        purchaseInvoices: [],
        purchaseInvoicesStatus: { loading: false, error: action.payload },
      };

    case 'FETCH_PURCHASE_INVOICE_REQUEST':
      return {
        ...state,
        selectedPurchaseInvoiceStatus: { loading: true, error: null },
      };
    case 'FETCH_PURCHASE_INVOICE_SUCCESS':
      return {
        ...state,
        selectedPurchaseInvoice: action.payload,
        selectedPurchaseInvoiceStatus: { loading: false, error: null },
      };
    case 'FETCH_PURCHASE_INVOICE_FAIL':
      return {
        ...state,
        selectedPurchaseInvoice: null,
        selectedPurchaseInvoiceStatus: { loading: false, error: action.payload },
      };

    case 'TRIGGER_PURCHASE_INVOICE_SYNC_REQUEST':
      return {
        ...state,
        purchaseInvoiceSyncStatus: { loading: true, error: null },
      };
    case 'TRIGGER_PURCHASE_INVOICE_SYNC_SUCCESS':
      return {
        ...state,
        purchaseInvoiceSyncStatus: { loading: false, error: null },
      };
    case 'TRIGGER_PURCHASE_INVOICE_SYNC_FAIL':
      return {
        ...state,
        purchaseInvoiceSyncStatus: { loading: false, error: action.payload },
      };

    case 'ENRICH_PURCHASE_INVOICE_REQUEST':
      return {
        ...state,
        selectedPurchaseInvoiceStatus: { loading: true, error: null },
      };
    case 'ENRICH_PURCHASE_INVOICE_SUCCESS':
      return {
        ...state,
        selectedPurchaseInvoiceStatus: { loading: false, error: null },
      };
    case 'ENRICH_PURCHASE_INVOICE_FAIL':
      return {
        ...state,
        selectedPurchaseInvoiceStatus: { loading: false, error: action.payload },
      };

    case 'REINCLUDE_PURCHASE_INVOICE_REQUEST':
      return {
        ...state,
        selectedPurchaseInvoiceStatus: { loading: true, error: null },
      };
    case 'REINCLUDE_PURCHASE_INVOICE_SUCCESS':
      return {
        ...state,
        selectedPurchaseInvoice: action.payload,
        purchaseInvoices: storeUpdateObjectInArray(state.purchaseInvoices, action.payload),
        selectedPurchaseInvoiceStatus: { loading: false, error: null },
      };
    case 'REINCLUDE_PURCHASE_INVOICE_FAIL':
      return {
        ...state,
        selectedPurchaseInvoiceStatus: { loading: false, error: action.payload },
      };

    case 'SET_PURCHASE_INVOICE_HOLD_REQUEST':
      return {
        ...state,
        selectedPurchaseInvoiceStatus: { loading: true, error: null },
      };
    case 'SET_PURCHASE_INVOICE_HOLD_SUCCESS':
      return {
        ...state,
        selectedPurchaseInvoice: action.payload,
        purchaseInvoices: storeUpdateObjectInArray(state.purchaseInvoices, action.payload),
        selectedPurchaseInvoiceStatus: { loading: false, error: null },
      };
    case 'SET_PURCHASE_INVOICE_HOLD_FAIL':
      return {
        ...state,
        selectedPurchaseInvoiceStatus: { loading: false, error: action.payload },
      };

    case 'DELETE_PURCHASE_INVOICE_SUCCESS':
      var deletedId = action.additionalData.invoiceId;
      return {
        ...state,
        purchaseInvoices: state.purchaseInvoices.filter(function (inv) {
          return String(inv.id) !== String(deletedId);
        }),
        selectedPurchaseInvoice: null,
      };

    // Consolidated Invoices
    case 'FETCH_CONSOLIDATED_INVOICES_REQUEST':
      return {
        ...state,
        consolidatedInvoicesStatus: { loading: true, error: null },
      };
    case 'FETCH_CONSOLIDATED_INVOICES_SUCCESS':
      return {
        ...state,
        consolidatedInvoices: action.payload.results || action.payload,
        consolidatedInvoicesPagination: action.payload.results
          ? omit(action.payload, 'results')
          : state.consolidatedInvoicesPagination,
        consolidatedInvoicesStatus: { loading: false, error: null },
      };
    case 'FETCH_CONSOLIDATED_INVOICES_FAIL':
      return {
        ...state,
        consolidatedInvoices: [],
        consolidatedInvoicesStatus: { loading: false, error: action.payload },
      };

    case 'FETCH_CONSOLIDATED_INVOICE_REQUEST':
      return {
        ...state,
        selectedConsolidatedInvoiceStatus: { loading: true, error: null },
      };
    case 'FETCH_CONSOLIDATED_INVOICE_SUCCESS':
      return {
        ...state,
        selectedConsolidatedInvoice: action.payload,
        selectedConsolidatedInvoiceStatus: { loading: false, error: null },
      };
    case 'FETCH_CONSOLIDATED_INVOICE_FAIL':
      return {
        ...state,
        selectedConsolidatedInvoice: null,
        selectedConsolidatedInvoiceStatus: { loading: false, error: action.payload },
      };

    case 'COMPOSE_CONSOLIDATED_INVOICE_REQUEST':
      return {
        ...state,
        composeInvoiceStatus: { loading: true, error: null },
      };
    case 'COMPOSE_CONSOLIDATED_INVOICE_SUCCESS':
      // The per-purchase-invoice branch returns {invoices, count}, not a
      // single invoice — only insert real invoice payloads (the list view
      // refetches on dialog close either way).
      return {
        ...state,
        consolidatedInvoices: action.payload && action.payload.id
          ? storeInsertItem(state.consolidatedInvoices, action.payload)
          : state.consolidatedInvoices,
        composeInvoiceStatus: { loading: false, error: null },
      };
    case 'COMPOSE_CONSOLIDATED_INVOICE_FAIL':
      return {
        ...state,
        composeInvoiceStatus: { loading: false, error: action.payload },
      };

    case 'MARK_CONSOLIDATED_INVOICE_READY_SUCCESS': {
      var updatedPreview = state.consolidatedInvoicePreview;
      if (updatedPreview && updatedPreview.header && action.payload && action.payload.status) {
        updatedPreview = {
          ...updatedPreview,
          header: { ...updatedPreview.header, status: action.payload.status },
        };
      }
      return {
        ...state,
        consolidatedInvoices: storeUpdateObjectInArray(state.consolidatedInvoices, action.payload),
        selectedConsolidatedInvoice: action.payload,
        consolidatedInvoicePreview: updatedPreview,
      };
    }

    case 'EXPORT_CONSOLIDATED_INVOICE_REQUEST':
      return {
        ...state,
        exportInvoiceStatus: { loading: true, error: null },
      };
    case 'EXPORT_CONSOLIDATED_INVOICE_SUCCESS': {
      var updatedPreviewExport = state.consolidatedInvoicePreview;
      if (updatedPreviewExport && updatedPreviewExport.header && action.payload && action.payload.status) {
        updatedPreviewExport = {
          ...updatedPreviewExport,
          header: {
            ...updatedPreviewExport.header,
            status: action.payload.status,
            exported_at: action.payload.exported_at,
            netvisor_sales_invoice_key: action.payload.netvisor_sales_invoice_key,
          },
        };
      }
      return {
        ...state,
        consolidatedInvoices: storeUpdateObjectInArray(state.consolidatedInvoices, action.payload),
        selectedConsolidatedInvoice: action.payload,
        consolidatedInvoicePreview: updatedPreviewExport,
        exportInvoiceStatus: { loading: false, error: null },
      };
    }
    case 'EXPORT_CONSOLIDATED_INVOICE_FAIL':
      return {
        ...state,
        exportInvoiceStatus: { loading: false, error: action.payload },
      };

    case 'DELETE_CONSOLIDATED_INVOICE_SUCCESS':
      return {
        ...state,
        consolidatedInvoices: storeRemoveItem(state.consolidatedInvoices, action.additionalData.invoiceId),
      };

    case 'UPDATE_CONSOLIDATED_INVOICE_DRAFT_REQUEST':
      return {
        ...state,
        updateConsolidatedInvoiceStatus: { loading: true, error: null },
      };
    case 'UPDATE_CONSOLIDATED_INVOICE_DRAFT_SUCCESS':
      // Preview is deliberately NOT patched here: grouping and subtotals must
      // come from the server, so the detail view refetches it after saving.
      return {
        ...state,
        consolidatedInvoices: storeUpdateObjectInArray(state.consolidatedInvoices, action.payload),
        selectedConsolidatedInvoice: action.payload,
        updateConsolidatedInvoiceStatus: { loading: false, error: null },
      };
    case 'UPDATE_CONSOLIDATED_INVOICE_DRAFT_FAIL':
      return {
        ...state,
        updateConsolidatedInvoiceStatus: { loading: false, error: action.payload },
      };

    case 'FETCH_COMPOSE_PREVIEW_REQUEST':
      return {
        ...state,
        composePreviewStatus: { loading: true, error: null },
      };
    case 'FETCH_COMPOSE_PREVIEW_SUCCESS':
      return {
        ...state,
        composePreview: action.payload,
        composePreviewStatus: { loading: false, error: null },
      };
    case 'FETCH_COMPOSE_PREVIEW_FAIL':
      return {
        ...state,
        composePreview: null,
        composePreviewStatus: { loading: false, error: action.payload },
      };
    case 'CLEAR_COMPOSE_PREVIEW':
      return {
        ...state,
        composePreview: null,
        composePreviewStatus: { loading: false, error: null },
      };

    case 'CREATE_CREDIT_NOTE_REQUEST':
      return {
        ...state,
        createdCreditNote: null,
        createCreditNoteStatus: { loading: true, error: null },
      };
    case 'CREATE_CREDIT_NOTE_SUCCESS':
      return {
        ...state,
        consolidatedInvoices: storeInsertItem(state.consolidatedInvoices, action.payload),
        createdCreditNote: action.payload,
        createCreditNoteStatus: { loading: false, error: null },
      };
    case 'CREATE_CREDIT_NOTE_FAIL':
      return {
        ...state,
        createCreditNoteStatus: { loading: false, error: action.payload },
      };

    case 'COPY_CONSOLIDATED_INVOICE_REQUEST':
      return {
        ...state,
        copiedInvoice: null,
        copyInvoiceStatus: { loading: true, error: null },
      };
    case 'COPY_CONSOLIDATED_INVOICE_SUCCESS':
      return {
        ...state,
        consolidatedInvoices: storeInsertItem(state.consolidatedInvoices, action.payload),
        copiedInvoice: action.payload,
        copyInvoiceStatus: { loading: false, error: null },
      };
    case 'COPY_CONSOLIDATED_INVOICE_FAIL':
      return {
        ...state,
        copyInvoiceStatus: { loading: false, error: action.payload },
      };

    case 'FETCH_CONSOLIDATED_INVOICE_PREVIEW_REQUEST':
      return {
        ...state,
        consolidatedInvoicePreviewStatus: { loading: true, error: null },
      };
    case 'FETCH_CONSOLIDATED_INVOICE_PREVIEW_SUCCESS':
      return {
        ...state,
        consolidatedInvoicePreview: action.payload,
        consolidatedInvoicePreviewStatus: { loading: false, error: null },
      };
    case 'FETCH_CONSOLIDATED_INVOICE_PREVIEW_FAIL':
      return {
        ...state,
        consolidatedInvoicePreview: null,
        consolidatedInvoicePreviewStatus: { loading: false, error: action.payload },
      };

    // Audit Logs
    case 'FETCH_CHAIN_AUDIT_LOGS_REQUEST':
      return { ...state, chainAuditLogsStatus: { loading: true, error: null } };
    case 'FETCH_CHAIN_AUDIT_LOGS_SUCCESS':
      return {
        ...state,
        chainAuditLogs: action.payload.results || action.payload,
        chainAuditLogsPagination: action.payload.results
          ? { count: action.payload.count, page_size: action.payload.page_size, current_page: action.payload.current_page }
          : state.chainAuditLogsPagination,
        chainAuditLogsStatus: { loading: false, error: null },
      };
    case 'FETCH_CHAIN_AUDIT_LOGS_FAIL':
      return { ...state, chainAuditLogs: [], chainAuditLogsStatus: { loading: false, error: action.payload } };

    default:
      return state;
  }
}
